import React, {useContext, useEffect, useState} from "react";
import {Box, Typography, Alert, Card} from "@mui/material";
import InputMask from 'react-input-mask';
import {errorManager, manageStringError} from "../../../common/methods/ApiService";
import {useTranslation} from "react-i18next";
import {PrimaryBigButton} from "../../../components/buttons/mainButton";
import {getAccessCode} from "../../../services/AccessCodeService";
import {TAccessCode} from "../../../interfaces/accessCode";
import moment from 'moment';
import {TCompany} from "../../../interfaces/company";
import {updateAdminCompany} from "../../../services/AdminService";
import {StoreContext} from "../../../common/struct/store";
import {AppContext} from "../../../App";
import {refreshToken} from "../../../services/AuthenticationService";
import {globalStoreReducer} from "../../../common/methods/context-setter/globalStoreReducer";
import {ADD_OPERATIONS, LOGIN} from "../../../common/methods/context-setter/globals";
import {getAllOperations} from "../../../services/OperationService";
import {useNavigate} from "react-router-dom";
import {operationUrl, PRIVATE_URL} from "../../../common/struct/urlManager";
import {isSuperAdmin} from "../../../common/struct/globalVar";

export default function SuperAdminCodes(): JSX.Element {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const STORE = useContext<StoreContext>(AppContext);
  const [store] = STORE;

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState({
    value: '',
    mask: '**** - **** - **** - ****'
  })
  const [accessCode, setAccessCode] = useState<TAccessCode|null>(null);

  useEffect(() => {
    if(!isSuperAdmin()) {
      navigate(PRIVATE_URL.LANDING_PAGE, {replace: true});
    }
  }, []);

  const onChange = (e: any): void => {
    setCode((prevState: any) => ({
      ...prevState,
      value: e.target.value
    }))
  }

  const selectCompany = (company: TCompany, operation: any): void => {
    updateAdminCompany(store.global.admin.id, '/bo/companies/'+company.id)
      .then((admin) => {
        refreshToken()
          .then(() => {
            globalStoreReducer(STORE, {type: LOGIN, company: admin.company})
            getAllOperations()
              .then(operations => {
                globalStoreReducer(STORE, {type: ADD_OPERATIONS, operations: operations});
                navigate(operationUrl(PRIVATE_URL.OPERATION_CUSTOMISATION, operation.id));
              })
              .catch(e => errorManager(e, t, STORE, navigate))
          })
      })
  }

  const submit = (e: any): void => {
    setLoading(true);
    e.preventDefault();
    getAccessCode(code.value.replace(/ /g, '').toUpperCase()).then((res: {totalItems: number, items: TAccessCode[]}) => {
      if (res.totalItems > 0) {
        setAccessCode(res.items[0]);
      }
    }).catch((error: any) => {
      manageStringError(error, setError, t);
    }).finally(() => {
      setLoading(false);
    })
  }

  return (
    <Box sx={{flex: 1, p: 4, display: "flex", alignItems: "center", flexDirection: "column", backgroundColor: "ornament.main", borderTopWidth: "2px", borderTopColor: "#E0E3E8", borderTopStyle: "solid"}}>
      <Box component="form" onSubmit={submit} sx={{width: "fit-content"}}>
        <Typography variant="body2" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Code d'accès</Typography>
        <Box sx={{mb: 4, display: "flex", alignItems: "center"}}>
          <Box sx={{width: "100%", backgroundColor: "ornament.light", border: "1px solid #BEBFC0", borderRadius: "4px", "input": {width: "100%", minWidth: "400px", borderRadius: "4px", border: "2px solid white", letterSpacing: "2px", backgroundColor: "inherit", textTransform: "uppercase", boxSizing: "border-box", padding: "14.5px 12px", fontSize: "18px", textAlign: "center", fontFamily: "Mulish", color: "#838384"}, "input:focus": {borderColor: "neutral.dark", borderWidth: "2px", outline: "none"}}}>
            <InputMask {...code} onChange={onChange} disabled={loading} placeholder={"XXXX - XXXX - XXXX - XXXX"}/>
          </Box>
          <PrimaryBigButton sx={{ml: 2}} submit={true} loading={loading} label={"Valider"}/>
        </Box>
        {error && <Alert severity="error" variant="filled" sx={{mb: 4, width: "initial !important", alignItems: "center", "a": {textDecoration: "none", fontWeight: "bold", color: "error.dark"}}}><span dangerouslySetInnerHTML={{__html: error}} /></Alert>}
      </Box>
      {accessCode && <Card sx={{minWidth: "800px", display: "flex", flexDirection: "column", mt: 5}}>
        <Box sx={{width: "100%", display: "flex", justifyContent: "space-between", borderBottomStyle: "solid", borderBottomWidth: "2px", borderBottomColor: "neutral.light", p: 3, boxSizing: "border-box"}}>
          <Box sx={{display: "flex"}}>
            <Box>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Entreprise</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.company.name}</Typography>
            </Box>
            <Box sx={{ml: 9}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Opération</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.operation.name}</Typography>
            </Box>
          </Box>
          <PrimaryBigButton action={(): void => selectCompany(accessCode.company, accessCode.operation)} loading={loading} label={"Voir l'opération"}/>
        </Box>
        <Box sx={{px: 3, py: 4, flex: 1, display: "flex", flexDirection: "column"}}>
          <Box sx={{width: "100%", display: "flex"}}>
            <Box>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Code</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.code}</Typography>
            </Box>
            <Box sx={{ml: 9}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Créé le</Typography>
              <Typography variant="body1" color="neutral.main">{moment(accessCode.createdAt).format('DD/MM/YYYY')}</Typography>
            </Box>
            <Box sx={{ml: 9}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Créé par</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.admin.name}</Typography>
            </Box>
          </Box>
          <Box sx={{width: "100%", display: "flex", mt: 7}}>
            <Box>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Expiration le</Typography>
              <Typography variant="body1" color="neutral.main">{moment(accessCode.expirationDate).format('DD/MM/YYYY')}</Typography>
            </Box>
            <Box sx={{ml: 9}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Montant</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.reward ? accessCode.reward.amount : 0} €</Typography>
            </Box>
            {accessCode.description && <Box sx={{ml: 9, maxWidth: "400px", maxHeight: "100px", overflow: "hidden"}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Description</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.description}</Typography>
            </Box>}
          </Box>
          <Box sx={{width: "100%", display: "flex", mt: 7}}>
            <Box>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Utilisé</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.consumptionDate ? "Oui" : "Non"}</Typography>
            </Box>
            {accessCode.consumptionDate && <Box sx={{ml: 9}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Utilisé par</Typography>
              <Typography variant="body1" color="neutral.main">{accessCode.operationParticipant ? accessCode.operationParticipant.participant.lastName+" "+accessCode.operationParticipant.participant.firstName : ""}</Typography>
            </Box>}
            {accessCode.consumptionDate && <Box sx={{ml: 9}}>
              <Typography variant="body1" color="neutral.main" sx={{fontWeight: "bold", mb: 1}}>Utilisé le</Typography>
              <Typography variant="body1" color="neutral.main">{moment(accessCode.consumptionDate).format('DD/MM/YYYY')}</Typography>
            </Box>}
          </Box>
        </Box>
      </Card>}
    </Box>
  );
}
